'use client'
import { useEffect, useState } from 'react'
import { useMediaQuery } from 'react-responsive'

import { StylePanelProps } from '@/lib/stylePanel'
import useResponsivePadding from '@/lib/useResponsivePadding'

import { PhnxImageHolder } from '../../01-atoms/ImageHolder/ImageHolder'
import styles from './CTAblock.module.css'

export type CTAblockProps = {
  title: string
  description?: string
  button?: React.ReactNode
  content?: React.ReactNode
  asset?: {
    src: string
    alt: string
    focus?: string
  }
  layout: 'default' | 'wide' | 'secondary' | 'tertiary' | 'quaternary' | 'quinary' | 'septenary'
  border?: boolean | undefined
  halfHalf?: boolean | undefined
  topHalfBackground?: any
  bottomHalfBackground?: any
  buttonBelow?: boolean
} & StylePanelProps

export const PhnxCTAblock: React.FC<CTAblockProps> = ({
  title,
  description,
  content,
  button,
  asset,
  layout,
  padding,
  backgroundColor,
  textColor,
  border = false,
  halfHalf = false,
  topHalfBackground,
  bottomHalfBackground,
  buttonBelow = false,
}) => {
  const paddingStyles = useResponsivePadding(padding)

  const style = {
    ...paddingStyles,
    backgroundColor,
    color: textColor
      ? textColor
      : layout === 'wide'
        ? '#FFFFFF'
        : layout === 'quaternary'
          ? '#FFFFFF'
          : '',
  }

  const isDesktop = useMediaQuery({ query: '(min-width: 600px)' })
  const [imageUri, setImageUri] = useState<string>('')

  useEffect(() => {
    if (layout !== 'secondary' && asset?.src) {
      const imageHeightWidth = isDesktop ? '1500x500' : '800x500'
      let imageUri = `${asset.src}/m/${imageHeightWidth}/filters:quality(80)`

      // if focus add focal point to image
      if (asset?.focus) {
        imageUri = `${imageUri}:focal(${asset.focus})`
      }
      setImageUri(imageUri)
    }
  }, [asset, isDesktop, layout])

  const containerClass =
    layout === 'default' ||
    layout === 'tertiary' ||
    layout === 'quaternary' ||
    layout === 'quinary' ||
    layout === 'septenary'
      ? 'container'
      : `layout-${layout}`
  const imageClass =
    layout === 'secondary' ||
    layout === 'quaternary' ||
    layout === 'quinary' ||
    layout === 'septenary'
      ? ''
      : asset?.src
        ? styles['has-image']
        : ''

  const halfHalfBackgroundColor =
    halfHalf && topHalfBackground && bottomHalfBackground
      ? `linear-gradient(to bottom, ${topHalfBackground} 50%, ${bottomHalfBackground} 50%)`
      : halfHalf && topHalfBackground
        ? `linear-gradient(to bottom, ${topHalfBackground} 50%, transparent 50%)`
        : halfHalf && bottomHalfBackground
          ? `linear-gradient(to bottom, transparent 50%, ${bottomHalfBackground} 50%)`
          : backgroundColor

  return (
    <div
      style={{
        ...style,
        background: halfHalfBackgroundColor,
      }}
    >
      <style jsx>{`
        stylepanel {
          ${paddingStyles}
        }
      `}</style>
      <div className={`${containerClass} ${styles[`cta-${layout}`]}`}>
        <div
          className={`${styles['ctablok']} ${styles[`layout-${layout}`]} ${imageClass} ${border ? styles['border'] : ''}`}
          style={{
            backgroundImage: `url("${imageUri}")`,
          }}
          data-testid="cta"
          data-layout={layout}
        >
          {layout === 'quaternary' && <div className={styles['backgroundShapeImage']} />}
          {layout === 'septenary' && (
            <>
              <div className={styles['backgroundShape']} />
            </>
          )}
          {layout === 'secondary' && asset && asset.src && (
            <div className={styles['cta-asset']}>
              <PhnxImageHolder
                images={[
                  {
                    src: asset.src,
                    alt: asset.alt,
                  },
                ]}
              />
            </div>
          )}
          {layout === 'quaternary' || layout === 'septenary' ? (
            <div className={styles.contentQuaternity}>
              <div className={styles.textQuaternity}>
                {title && (
                  <h2 className={styles['title']} style={{ color: textColor }}>
                    {title}
                  </h2>
                )}
                {description && (
                  <p className={styles['description']} style={{ color: textColor }}>
                    {description}
                  </p>
                )}
                {content && (
                  <div className={styles['content']} style={{ color: textColor }}>
                    {content}
                  </div>
                )}
                {button && buttonBelow && (
                  <div
                    className={`${styles['buttonQuaternity']} ${styles['button-below']}`}
                    style={{
                      justifyContent: 'flex-start',
                    }}
                  >
                    {button}
                  </div>
                )}
              </div>

              {button && !buttonBelow && <div className={styles['buttonQuaternity']}>{button}</div>}
            </div>
          ) : (
            <>
              {title && (
                <h2 className={styles['title']} style={{ color: textColor }}>
                  {title}
                </h2>
              )}
              {description && (
                <p className={styles['description']} style={{ color: textColor }}>
                  {description}
                </p>
              )}
              {button && !buttonBelow && <div>{button}</div>}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
